import StaffingDestinationPageSchema, {
  StaffingDestinationPageJSON,
} from "work/staffing/_schemas/staffing-destination-page/staffing-destination-page-schema";
import StaffingDestination from "work/staffing/staffing-destination";

/**
 * One page of the work the signed-in viewer may put somebody onto.
 *
 * The server decides what is on the page and how many there are in total. Nothing here recounts
 * either: a picker that inferred a total from the rows it happened to receive would be reporting
 * the page size as the answer.
 */
export default class StaffingDestinationPage {
  public static readonly EMPTY: StaffingDestinationPage = new StaffingDestinationPage(
    [],
    0,
    10,
    0,
    true,
  );

  public static fromJSON(json: StaffingDestinationPageJSON): StaffingDestinationPage {
    const params = StaffingDestinationPageSchema.deserialize(json);
    return new StaffingDestinationPage(
      params.destinations.map((destination) => new StaffingDestination(destination)),
      params.pageIndex,
      params.pageSize,
      params.totalCount,
      params.totalIsExact,
    );
  }

  public readonly destinations: readonly StaffingDestination[];
  public readonly pageIndex: number;
  public readonly pageSize: number;
  public readonly totalCount: number;
  public readonly totalIsExact: boolean;

  /** Whether the viewer may staff nothing at all, rather than nothing matching a search. */
  public get isEmpty(): boolean {
    return this.totalCount === 0;
  }

  /** Whether there is a page after this one. */
  public get hasNextPage(): boolean {
    return (this.pageIndex + 1) * this.pageSize < this.totalCount;
  }

  /**
   * How many pages the pager draws numbers for.
   *
   * **This is exact even when `totalIsExact` is false, and that is not a contradiction.**
   * `totalCount` is how many destinations the server actually established — the rows it can serve
   * — not how many exist. When the eligibility window overflowed there may be more work out there
   * that was never evaluated, but the server cannot hand any of it over, so a page past this one
   * would come back empty whatever the real number is. The page strip is therefore complete for
   * what is reachable, and `countLabel`'s `+` is what says the set was cut short.
   *
   * One page rather than none when there is nothing, because a pager with a count of zero draws
   * as a broken control rather than as an absent one.
   */
  public get pageCount(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  /** Whether there is a page before this one. */
  public get hasPreviousPage(): boolean {
    return this.pageIndex > 0;
  }

  /** The index of the first row on this page, counting from one, for a human to read. */
  public get firstRowNumber(): number {
    return this.destinations.length === 0 ? 0 : this.pageIndex * this.pageSize + 1;
  }

  /** The index of the last row on this page, counting from one. */
  public get lastRowNumber(): number {
    return this.pageIndex * this.pageSize + this.destinations.length;
  }

  /**
   * How much of the whole this page is, in the words the picker shows.
   *
   * `N+` when the server said its count is a floor, because a number presented as a total when it
   * is not one is worse than an obviously approximate one.
   */
  public get countLabel(): string {
    if (this.isEmpty) return "";
    const total = this.totalIsExact ? `${this.totalCount}` : `${this.totalCount}+`;
    return `Showing ${this.firstRowNumber}–${this.lastRowNumber} of ${total}`;
  }

  /**
   * What to add to `countLabel` when the count is a floor, and nothing when it is not.
   *
   * The `+` alone says the number is approximate; it does not say what a reader can do about it.
   * The server stopped evaluating at its eligibility window, and the one thing that gets past that
   * is a narrower search — so the caption says so rather than leaving somebody paging to the end
   * of a list that was cut short before they got there.
   */
  public get truncationHint(): string {
    return this.totalIsExact ? "" : "Search to narrow the list — more may match than are shown.";
  }

  public constructor(
    destinations: readonly StaffingDestination[],
    pageIndex: number,
    pageSize: number,
    totalCount: number,
    totalIsExact: boolean,
  ) {
    this.destinations = destinations;
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;
    this.totalCount = totalCount;
    this.totalIsExact = totalIsExact;
  }

}
